import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, createUserWithEmailAndPassword, updateProfile } from "firebase/auth";
import { getFirestore, doc, setDoc } from "firebase/firestore";
import BlurText from './BlurText';
import './QuestionnaireSuccess.css';
import { trackEvent } from './Analytics';
import platesImage from '../assets/plates.png'; // Import the plates image

const QuestionnaireSuccess = () => {
    const navigate = useNavigate();
    const [showSignupForm, setShowSignupForm] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        name: '',
        company: '',
        companySize: '',
        phone: '',
        country: '', 
        marketingConsent: false
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');
    const [passwordValidation, setPasswordValidation] = useState({
        length: false,
        hasUppercase: false,
        hasLowercase: false,
        hasNumber: false
    });
    const [passwordTouched, setPasswordTouched] = useState(false);
    
    // Keep the debug logging
    useEffect(() => {
        console.log('[SOPHIE-CLIENT] QuestionnaireSuccess component mounted');
        
        // Log Firebase availability
        try {
            const db = getFirestore();
            console.log('[SOPHIE-CLIENT] Firestore initialized successfully:', db ? 'Available' : 'Undefined');
        } catch (error) {
            console.error('[SOPHIE-CLIENT] Firebase initialization check error:', error);
        }
        
        return () => {
            console.log('[SOPHIE-CLIENT] QuestionnaireSuccess component unmounted');
        };
    }, []);

    // Remove wave-related state and functions
    
    const handleNoClick = () => {
        console.log('[SOPHIE-CLIENT] User clicked "No thanks"');
        navigate('/');
    };

    const handleYesClick = () => {
        console.log('[SOPHIE-CLIENT] User clicked "Yes, I\'m interested"');
        setShowSignupForm(true);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        if (name === 'password') {
            setPasswordTouched(true);
        }
        
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    // Keep password validation function
    useEffect(() => {
        if (formData.password) {
            setPasswordValidation({
                length: formData.password.length >= 8,
                hasUppercase: /[A-Z]/.test(formData.password),
                hasLowercase: /[a-z]/.test(formData.password),
                hasNumber: /[0-9]/.test(formData.password)
            });
        }
    }, [formData.password]);

    // Keep getPasswordStrength function
    const getPasswordStrength = () => {
        const criteria = Object.values(passwordValidation);
        const passedCriteria = criteria.filter(Boolean).length;
        
        if (passedCriteria === 0) return 'very-weak';
        if (passedCriteria <= 1) return 'weak';
        if (passedCriteria <= 3) return 'medium';
        return 'strong';
    };

    // Keep form submission function
    const handleSubmit = async (e) => {
        // Existing submission handler code remains the same
        // ...
        e.preventDefault();
        
        console.log('[SOPHIE-CLIENT] Form submission started');
        
        const passwordStrength = getPasswordStrength();
        if (passwordStrength === 'very-weak' || passwordStrength === 'weak') {
            console.warn('[SOPHIE-CLIENT] Password validation failed: Strength is', passwordStrength);
            setErrorMessage('Please create a stronger password that meets more criteria.');
            setSubmitStatus('error');
            return;
        }
        
        setIsSubmitting(true);
        setErrorMessage('');
        
        try {
            // Rest of the submission handler code remains the same
            // ...
            
            // Initialize Firebase Auth
            console.log('[SOPHIE-CLIENT] Initializing Firebase Auth');
            const auth = getAuth();
            
            // Create user with email and password
            console.log('[SOPHIE-CLIENT] Creating user account with email', formData.email.substring(0, 3) + '...');
            const userCredential = await createUserWithEmailAndPassword(
                auth, 
                formData.email, 
                formData.password
            );
            
            // Get the newly created user
            const user = userCredential.user;
            console.log("[SOPHIE-CLIENT] User created successfully:", user.uid);
            
            // Update user profile with display name
            console.log('[SOPHIE-CLIENT] Updating user profile with display name');
            await updateProfile(user, {
                displayName: formData.name
            });
            console.log("[SOPHIE-CLIENT] User profile updated successfully");
            
            // Initialize Firestore
            console.log('[SOPHIE-CLIENT] Initializing Firestore');
            const db = getFirestore();
            console.log("[SOPHIE-CLIENT] Firestore initialized:", db ? 'Successfully' : 'Failed');
            
            // Prepare user data for Firestore (excluding password)
            const userData = {
                uid: user.uid,
                email: formData.email,
                name: formData.name,
                company: formData.company,
                companySize: formData.companySize,
                phone: formData.phone,
                country: formData.country,
                marketingConsent: formData.marketingConsent,
                createdAt: new Date(),
                lastLogin: new Date(),
                source: 'questionnaire_success'
            };
            
            try {
                // Store in Firestore
                console.log("[SOPHIE-CLIENT] Attempting to write to Firestore collection 'user_data' with doc ID:", formData.email);
                
                // Log the document reference before attempting to write
                const docRef = doc(db, "user_data", formData.email);
                console.log("[SOPHIE-CLIENT] Document reference created:", docRef ? 'Valid' : 'Invalid');
                
                await setDoc(docRef, userData);
                console.log("[SOPHIE-CLIENT] Firestore document written successfully");
            } catch (firestoreError) {
                console.error("[SOPHIE-CLIENT] Firestore write error:", firestoreError);
                console.error("[SOPHIE-CLIENT] Error code:", firestoreError.code);
                console.error("[SOPHIE-CLIENT] Error message:", firestoreError.message);
                console.error("[SOPHIE-CLIENT] Error stack:", firestoreError.stack);
                
                // Try an alternative approach if needed
                // ...
            }
            
            // Track signup event
            console.log('[SOPHIE-CLIENT] Tracking signup event');
            trackEvent('user_signup', {
                method: 'email',
                source: 'questionnaire_success'
            });
            
            setSubmitStatus('success');
            
            // Redirect to homepage after success
            console.log('[SOPHIE-CLIENT] Scheduling redirect to homepage');
            setTimeout(() => {
                navigate('/');
            }, 3000);
        } catch (error) {
            console.error('[SOPHIE-CLIENT] Error creating account:', error);
            console.error('[SOPHIE-CLIENT] Error code:', error.code);
            console.error('[SOPHIE-CLIENT] Error message:', error.message);
            console.error('[SOPHIE-CLIENT] Error stack:', error.stack);
            
            // Replace specific Firebase auth errors with user-friendly messages
            let errorMsg = 'There was an error creating your account. Please try again.';
            
            switch(error.code) {
                case 'auth/email-already-in-use':
                    errorMsg = 'This email address is already registered. Please use a different email or try to log in.';
                    break;
                case 'auth/invalid-email':
                    errorMsg = 'Please enter a valid email address.';
                    break;
                case 'auth/weak-password':
                    errorMsg = 'Please use a stronger password (at least 8 characters).';
                    break;
                default:
                    errorMsg = 'An unexpected error occurred. Please try again later.';
            }
            
            setErrorMessage(errorMsg);
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
            console.log('[SOPHIE-CLIENT] Form submission process completed');
        }
    };

    return (
        <main className="modern-layout">
            {/* Replace wave container with gradient background */}
            <div className="gradient_background">
                {/* Add plates image as background */}
                <img src={platesImage} alt="" className="background-plates" aria-hidden="true" />
                
                <div className="content-container">
                    <div className="success-wrapper">
                        {!showSignupForm ? (
                            <>
                                <div className="thank-you-container">
                                    <BlurText
                                        text="Thank you for completing the questionnaire!"
                                        delay={100}
                                        animateBy="words"
                                        direction="top"
                                        className="thank-you-title"
                                    />
                                    <p className="thank-you-subtitle">Your insights are valuable to us.</p>
                                </div>
                                
                                <div className="prompt-container">
                                    <h2>Do you want to make your CRM better with AI?</h2>
                                    <p>Get trial access to Sophie AI Meeting recorder tool that automatically updates meeting summaries to your CRM</p>
                                    
                                    <div className="prompt-buttons">
                                        <button onClick={handleNoClick} className="prompt-button no-button">
                                            No, thanks
                                        </button>
                                        <button onClick={handleYesClick} className="prompt-button yes-button">
                                            Yes, I'm interested
                                        </button>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="signup-form-container">
                                <h2>Create your Sophie AI account</h2>
                                <p>Complete this form to get started with Sophie AI tools. We'll send you access instructions within one business day.</p>
                                
                                {/* Form content remains unchanged */}
                                <form onSubmit={handleSubmit} className="signup-form">
                                    {/* Form fields remain unchanged */}
                                    <div className="form-group">
                                        <label htmlFor="name">Full Name</label>
                                        <input
                                            type="text"
                                            id="name"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                    
                                    <div className="form-group">
                                        <label htmlFor="email">Email Address</label>
                                        <input
                                            type="email"
                                            id="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                    
                                    <div className="form-group">
                                        <label htmlFor="password">Password</label>
                                        <input
                                            type="password"
                                            id="password"
                                            name="password"
                                            value={formData.password}
                                            onChange={handleChange}
                                            required
                                            minLength="8"
                                        />
                                        
                                        {passwordTouched && (
                                            <div className="password-feedback">
                                                <div className={`password-strength-meter ${getPasswordStrength()}`}>
                                                    <div className="strength-label">Password strength: {getPasswordStrength()}</div>
                                                    <div className="strength-bar">
                                                        <div className={`strength-indicator ${getPasswordStrength()}`}></div>
                                                    </div>
                                                </div>
                                                
                                                <ul className="password-requirements">
                                                    <li className={passwordValidation.length ? 'met' : 'not-met'}>
                                                        At least 8 characters
                                                    </li>
                                                    <li className={passwordValidation.hasUppercase ? 'met' : 'not-met'}>
                                                        Contains uppercase letter
                                                    </li>
                                                    <li className={passwordValidation.hasLowercase ? 'met' : 'not-met'}>
                                                        Contains lowercase letter
                                                    </li>
                                                    <li className={passwordValidation.hasNumber ? 'met' : 'not-met'}>
                                                        Contains number
                                                    </li>
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                    
                                    <div className="form-group">
                                        <label htmlFor="company">Company Name</label>
                                        <input
                                            type="text"
                                            id="company"
                                            name="company"
                                            value={formData.company}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                    
                                    <div className="form-group">
                                        <label htmlFor="companySize">Company Size</label>
                                        <select
                                            id="companySize"
                                            name="companySize"
                                            value={formData.companySize}
                                            onChange={handleChange}
                                            required
                                        >
                                            <option value="">Select company size</option>
                                            <option value="small">Small (1-50 employees)</option>
                                            <option value="medium">Medium (51-500 employees)</option>
                                            <option value="large">Large (501+ employees)</option>
                                        </select>
                                    </div>
                                    
                                    {/* Add country field here */}
                                    <div className="form-group">
                                        <label htmlFor="country">Country</label>
                                        <input
                                            type="text"
                                            id="country"
                                            name="country"
                                            value={formData.country}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                    
                                    <div className="form-group">
                                        <label htmlFor="phone">Phone Number</label>
                                        <input
                                            type="tel"
                                            id="phone"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                    
                                    <div className="form-group checkbox-group">
                                        <input
                                            type="checkbox"
                                            id="marketingConsent"
                                            name="marketingConsent"
                                            checked={formData.marketingConsent}
                                            onChange={handleChange}
                                        />
                                        <label htmlFor="marketingConsent">
                                            I agree to receive marketing emails from Sophie AI
                                        </label>
                                    </div>
                                    
                                    <button 
                                        type="submit" 
                                        className="submit-button" 
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? 'Creating account...' : 'Create Free Account'}
                                    </button>
                                    
                                    {submitStatus === 'success' && (
                                        <div className="success-message">
                                            Your account was created successfully! Redirecting to homepage...
                                        </div>
                                    )}
                                    
                                    {submitStatus === 'error' && (
                                        <div className="error-message">
                                            {errorMessage}
                                        </div>
                                    )}
                                </form>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </main>
    );
};

export default QuestionnaireSuccess;